<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { TruckIcon, UserGroupIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
  reference: { type: Object, required: true },
  version: { type: Object, default: () => ({}) },
});
const route = useRoute();
const isPts = computed(() => {
  const type = props.reference.type ?? {};
  const names = [type.shortname, type.name].map((value) =>
    String(value ?? "")
      .trim()
      .toLowerCase(),
  );
  return (
    type.prefix === "M1P" ||
    names.some((value) =>
      [
        "pts",
        "patient transportation",
        "patient transport",
        "patient transfer",
      ].includes(value),
    )
  );
});
const staff = computed(() => {
  const members = new Map();
  (props.version.dates ?? []).forEach((date) => {
    (date.resources ?? []).forEach((resource) => {
      (resource.personnel_assignments ?? []).forEach((assignment) => {
        const id = assignment.staff_id ?? assignment.staff?.id;
        if (!id) return;
        const name = assignment.staff?.contact?.name?.trim() || `Staff #${id}`;
        members.set(String(id), { id, name });
      });
    });
  });
  return [...members.values()].sort((left, right) =>
    left.name.localeCompare(right.name),
  );
});
const vehicleMaximum = computed(() => {
  const value = props.reference.vehicle_allocation_limit?.quoted_max;
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === "" ||
    typeof value === "boolean"
  )
    return null;
  const maximum = Number(value);
  return Number.isInteger(maximum) && maximum >= 0 ? maximum : null;
});
const allocationLink = computed(() => ({
  path: `/reference/${props.reference.id}`,
  query: { ...route.query, tab: "resources" },
  hash: "#resources",
}));
const initials = (name) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
</script>

<template>
  <div
    v-if="isPts || vehicleMaximum !== null"
    class="space-y-4 border-t border-slate-200 pt-5 dark:border-slate-700"
  >
    <section
      v-if="isPts"
      aria-labelledby="overview-staff-heading"
      data-testid="overview-assigned-staff"
    >
      <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3
          id="overview-staff-heading"
          class="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100"
        >
          <UserGroupIcon
            class="h-4 w-4 text-slate-500 dark:text-slate-400"
            aria-hidden="true"
          />
          Assigned staff
          <span
            class="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            >{{ staff.length }}</span
          >
        </h3>
        <router-link :to="allocationLink" class="overview-action"
          >Manage staff</router-link
        >
      </div>
      <ul
        v-if="staff.length"
        class="overview-staff-list"
        aria-label="Assigned staff"
      >
        <li v-for="member in staff" :key="member.id" class="min-w-0">
          <router-link
            :to="`/staff/${member.id}`"
            class="flex min-h-12 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-900 transition hover:border-sky-300 hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-sky-950/30"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-300"
              aria-hidden="true"
              >{{ initials(member.name) }}</span
            >
            <span class="min-w-0 break-words">{{ member.name }}</span>
          </router-link>
        </li>
      </ul>
      <p
        v-else
        class="rounded-xl border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400"
      >
        No staff assigned yet.
      </p>
    </section>

    <section
      v-if="vehicleMaximum !== null"
      aria-labelledby="overview-vehicles-heading"
      data-testid="overview-vehicle-allowance"
      class="rounded-xl border border-slate-200 px-4 py-3 dark:border-slate-700"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3
            id="overview-vehicles-heading"
            class="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400"
          >
            <TruckIcon class="h-4 w-4" aria-hidden="true" /> Vehicle allowance
          </h3>
          <p
            class="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100"
          >
            {{ vehicleMaximum }}
            {{ vehicleMaximum === 1 ? "vehicle" : "vehicles" }}
            <span class="text-sm font-normal text-slate-500 dark:text-slate-400"
              >maximum</span
            >
          </p>
          <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Included in the accepted quote
          </p>
        </div>
        <router-link
          v-if="reference.vehicle_allocation_limit?.quote_id"
          :to="`/quotes/${reference.vehicle_allocation_limit.quote_id}`"
          class="overview-action"
          >Quote #{{ reference.vehicle_allocation_limit.quote_id }}</router-link
        >
      </div>
    </section>
  </div>
</template>

<style scoped>
.overview-staff-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 11rem), 1fr));
  gap: 0.5rem;
}
.overview-action {
  @apply inline-flex min-h-9 items-center rounded-lg text-xs font-semibold text-sky-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 dark:text-sky-300 dark:focus-visible:ring-offset-slate-800;
}
</style>
